import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineGrid,
  InlineStack,
  Listbox,
  Page,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { formatPlate } from "../../utility/funcs";
import { UserAuth } from "../../context/AuthContext";
import FieldLinkShare, { unitLabel } from "./FieldLinkShare";

// Pre-create a service for (car, provider) and hand the provider a link to
// document it from their phone (FieldCapture). The service is origin='field',
// status 'awaiting_provider', total 0 and no items until staff attach the CFDI.
function NewFieldService() {
  const navigate = useNavigate();
  const { hasPermission } = UserAuth();

  const [plate, setPlate] = useState("");
  const [car, setCar] = useState(null);
  const [carError, setCarError] = useState(null);
  const [carLoading, setCarLoading] = useState(false);

  const [providerQuery, setProviderQuery] = useState("");
  const [providerResults, setProviderResults] = useState([]);
  const [providerLoading, setProviderLoading] = useState(false);
  const [provider, setProvider] = useState(null);
  const searchTimeout = useRef(null);

  const [categories, setCategories] = useState([]);
  const [expectedCats, setExpectedCats] = useState([]);
  const [instructions, setInstructions] = useState("");

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // { serviceId, token }

  useEffect(() => {
    supabase
      .from("field_service_categories")
      .select("key, label, hint")
      .eq("active", true)
      .order("sort")
      .then(({ data }) => setCategories(data || []));
    return () => clearTimeout(searchTimeout.current);
  }, []);

  const searchCar = async (e) => {
    e?.preventDefault();
    const q = plate.trim().toUpperCase();
    if (!q) return;
    setCarLoading(true);
    setCarError(null);
    const { data } = await supabase
      .from("cars")
      .select("id, plate, vin, year, color, model(name, image_url, brand(name))")
      .or(`plate.eq.${q},vin.eq.${q}`)
      .limit(1);
    setCarLoading(false);
    if (data?.length) setCar(data[0]);
    else setCarError("No se encontró la placa o VIN en la flotilla.");
  };

  const onProviderQuery = (value) => {
    setProviderQuery(value);
    setProvider(null);
    clearTimeout(searchTimeout.current);
    if (!value.trim()) {
      setProviderResults([]);
      return;
    }
    setProviderLoading(true);
    searchTimeout.current = setTimeout(async () => {
      const { data } = await supabase.rpc("search_service_providers", {
        search_text: value.trim(),
      });
      setProviderResults(data || []);
      setProviderLoading(false);
    }, 400);
  };

  const create = async () => {
    setCreating(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("create_field_service", {
      p_car: car.id,
      p_provider: provider.id,
      p_expected_categories: expectedCats,
      p_instructions: instructions,
    });
    setCreating(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    setCreated({ serviceId: row.service_id, code: row.code });
  };

  if (created)
    return (
      <AppWrapper>
        <Page title="Servicio creado" backAction={{ url: "/services" }}>
          <Card>
            <BlockStack gap="400">
              <Banner tone="success">
                Servicio #{created.serviceId} creado para {formatPlate(car.plate)} con{" "}
                {provider.name}. Envía la liga al proveedor para que capture la
                evidencia desde su celular.
              </Banner>
              <FieldLinkShare
                code={created.code}
                unit={unitLabel({ modelName: car.model?.name, plate: car.plate, year: car.year })}
              />
              <InlineStack gap="200">
                <Button url={`/services/${created.serviceId}`}>Ver servicio</Button>
                <Button variant="plain" onClick={() => window.location.reload()}>
                  Crear otro
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Page>
      </AppWrapper>
    );

  const carTitle = car
    ? [car.model?.brand?.name, car.model?.name, car.year].filter(Boolean).join(" ")
    : "";

  return (
    <AppWrapper>
      <Page
        title="Servicio por liga"
        subtitle="El proveedor registra placa, odómetro y evidencia desde su celular"
        backAction={{ onAction: () => navigate(-1) }}
        primaryAction={{
          content: "Crear y obtener liga",
          loading: creating,
          disabled: !car || !provider || !hasPermission("services.create"),
          onAction: create,
        }}
      >
        <BlockStack gap="400">
          {error && <Banner tone="critical">{error}</Banner>}
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Vehículo</Text>
                {car ? (
                  <InlineStack gap="300" blockAlign="center" wrap={false}>
                    {car.model?.image_url && (
                      <img
                        src={car.model.image_url}
                        alt=""
                        style={{ width: 90, height: 60, objectFit: "contain" }}
                      />
                    )}
                    <BlockStack gap="100">
                      <Text fontWeight="semibold">{carTitle}</Text>
                      <Text tone="subdued">
                        {formatPlate(car.plate)} · {car.vin}
                      </Text>
                      <Button variant="plain" onClick={() => setCar(null)}>
                        Cambiar
                      </Button>
                    </BlockStack>
                  </InlineStack>
                ) : (
                  <form onSubmit={searchCar}>
                    <InlineStack gap="200" wrap={false}>
                      <div style={{ flexGrow: 1 }}>
                        <TextField
                          autoComplete="off"
                          placeholder="Placa o VIN"
                          value={plate}
                          onChange={(v) => setPlate(v.toUpperCase())}
                          error={carError}
                        />
                      </div>
                      <Button icon={SearchIcon} submit loading={carLoading} />
                    </InlineStack>
                  </form>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Proveedor</Text>
                {provider ? (
                  <BlockStack gap="100">
                    <Text fontWeight="semibold">{provider.name}</Text>
                    <Text tone="subdued">
                      {[provider.contact_name, provider.phone].filter(Boolean).join(" · ") ||
                        "Sin contacto registrado"}
                    </Text>
                    <Button variant="plain" onClick={() => onProviderQuery("")}>
                      Cambiar
                    </Button>
                  </BlockStack>
                ) : (
                  <BlockStack gap="200">
                    <TextField
                      autoComplete="off"
                      placeholder="Buscar proveedor…"
                      value={providerQuery}
                      onChange={onProviderQuery}
                      suffix={providerLoading ? <Spinner size="small" /> : null}
                    />
                    {providerResults.length > 0 && (
                      <Listbox
                        onSelect={(id) => {
                          const p = providerResults.find((r) => String(r.id) === id);
                          setProvider(p);
                          setProviderQuery(p.name);
                          setProviderResults([]);
                        }}
                      >
                        {providerResults.map((p) => (
                          <Listbox.Option key={p.id} value={String(p.id)}>
                            <div style={{ padding: "4px 8px" }}>
                              <strong>{p.name}</strong>
                              <div style={{ fontSize: "0.8em", color: "#666" }}>
                                {[p.contact_name, p.city, p.phone].filter(Boolean).join(" · ")}
                              </div>
                            </div>
                          </Listbox.Option>
                        ))}
                      </Listbox>
                    )}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </InlineGrid>

          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Servicios solicitados (opcional)</Text>
              <Text tone="subdued">
                El proveedor los ve marcados como "Solicitado", pero tiene que
                seleccionar él mismo lo que realmente hizo. Si no reporta alguno,
                se marca en el servicio.
              </Text>
              <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="200">
                {categories.map((c) => (
                  <Checkbox
                    key={c.key}
                    label={c.label}
                    helpText={c.hint || undefined}
                    checked={expectedCats.includes(c.key)}
                    onChange={(v) =>
                      setExpectedCats((prev) =>
                        v ? [...prev, c.key] : prev.filter((x) => x !== c.key),
                      )
                    }
                  />
                ))}
              </InlineGrid>
              <TextField
                label="Indicaciones para el proveedor (opcional)"
                multiline={3}
                autoComplete="off"
                value={instructions}
                onChange={setInstructions}
                placeholder="Ej. Revisar ruido en suspensión delantera; usar aceite 5W-30 sintético."
              />
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default NewFieldService;
